import React, { createContext, useEffect, useState } from 'react'
import moment from 'moment'
import { useParams } from 'react-router-dom'
import API from '../../../services/API'
import DetailsBox from '../../../components/Cards/DetailsBox'

//@MUI
import Alert from '@mui/material/Alert'
import Avatar from '@mui/material/Avatar'
import AvatarGroup from '@mui/material/AvatarGroup'
import Grid from '@mui/material/Grid'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import Paper from '@mui/material/Paper'
import TipTap from '../../../components/TipTap/TipTap'
import UnsavedChanges from '../../../components/UnsavedChanges/UnsavedChanges'
import Edit from '@mui/icons-material/Edit'
import Agenda from './Agenda'
import MeetingDetails from './MeetingDetails'
import { useDocumentTitle } from '../../../helpers/DocumentTitleManager'

export const MeetingPageContext = createContext({})

const MeetingPage = () => {
  const { id: meetingID } = useParams('')
  const {setPageTitle} = useDocumentTitle()
  const [meetingNotFound, setMeetingNotFound] = useState(false)
  const [meeting, setMeeting] = useState({})
  const [agendas, setAgendas] = useState([])
  const [expandedAgendaAccordions, setExpandedAgendaAccordions] = useState([0])
  const [meetingNotes, setMeetingNotes] = useState('')
  const [changesPresent, setChangesPresent] = useState(false)
  const [openMeetingDetails, setOpenMeetingDetails] = useState(false)

  useEffect(() => {
    API.get('/governance/meetings/' + meetingID).then((res) => {
      if (res.status === 404) setMeetingNotFound(true)
      else if (res.status === 200) {
        setMeeting(() => {
          const data = { ...res.data }
          delete data.meetingNotes
          delete data.agenda
          return data
        })
        setMeetingNotes(res.data.meetingNotes)
        setAgendas(res.data.agenda)
        setExpandedAgendaAccordions(res.data.agenda.map((x) => x.title))
      }
    })
  }, [meetingID])

  const saveChanges = () => {
    API.patch(`/governance/meetings/${meetingID}`, {
      agenda: agendas,
      meetingNotes,
    }).then((res) => {
      if (res.status === 200) {
        setChangesPresent(false)
      }
    })
  }

  useEffect(() => {
    if (meeting?.title) {
      setPageTitle(meeting.title)
    }
  }, [meeting?.title, setPageTitle])

  return (
    <MeetingPageContext
      value={{
        meeting,
        setMeeting,
        expandedAgendaAccordions,
        setExpandedAgendaAccordions,
        setAgendas,
        agendas,
        setChangesPresent,
        openMeetingDetails,
        setOpenMeetingDetails,
      }}
    >
      <Stack gap={1}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="title2">
              {meeting?.meetingType} Meeting
            </Typography>
            {meetingNotFound && (
              <Alert severity="error">
                No such meeting! Please check the URL.
              </Alert>
            )}
          </Grid>
          <Grid item xs={12}>
            <DetailsBox
              title="Details"
              component={Paper}
              padding={1}
              rows={[
                {
                  label: 'Title',
                  value: meeting.title,
                },
                {
                  label: 'Date',
                  value: moment(meeting.startTime).format(
                    'Do MMMM YYYY - HH:mm'
                  ),
                },
                { label: 'Location', value: meeting.location },
                {
                  label: 'Invitees',
                  value: meeting.invitees && (
                    <AvatarGroup>
                      {meeting.invitees.map((person) => (
                        <Tooltip
                          key={person._id}
                          title={person.firstName + ' ' + person.lastName}
                        >
                          <Avatar
                            src={person.photograph}
                            sx={{ width: 16, height: 16, borderRadius: 1 }}
                          />
                        </Tooltip>
                      ))}
                    </AvatarGroup>
                  ),
                },
              ]}
              buttonRight={
                <IconButton
                  onClick={() => setOpenMeetingDetails(true)}
                  size="small"
                >
                  <Edit color="anMainBlue" fontSize="small" />
                </IconButton>
              }
            ></DetailsBox>
            <MeetingDetails />
          </Grid>
          <Grid item xs={12} lg={8} order={{ xs: 2, lg: 1 }}>
            <Stack gap={1} component={Paper} padding={1}>
              <Typography variant="title2">Meeting Notes</Typography>
              <TipTap
                value={meetingNotes}
                onChange={(newValue) => {
                  setMeetingNotes(newValue)
                  !changesPresent && setChangesPresent(true)
                }}
              />
            </Stack>
          </Grid>
          <Agenda />
        </Grid>
      </Stack>
      <UnsavedChanges
        changesPresent={changesPresent}
        handleSave={saveChanges}
      />
    </MeetingPageContext>
  )
}

export default MeetingPage
